/**
 * Moving a wall held by an attached room while measuring (PRP-SKETCH-FIELD-4 K15, N4).
 *
 * A measurement between two walls that are both shared with other rooms used to be
 * refused ("Both walls are held", FIELD-1 F5). K15 (founder, 2026-10-19):
 *  1. If one of the two walls is the split line with the room this room was split
 *     from, that wall moves. This room changes and the split-from room changes by the
 *     same amount the other way; the wall to any other neighbour stays put. Swap does
 *     not override this (DD-SF4.1).
 *  2. If neither (or both) is a split line, the user picks which wall moves; the room
 *     across the chosen wall follows by the same amount (the FIELD-3 J17 shared-wall
 *     move).
 *
 * How a split line is known (no stored field added): `doSplit` writes the split line as
 * a missing opening on the 0 in shared wall whose J1 record (`missing.absorbed`) names
 * both rooms with `distanceIn: 0` (FIELD-3 J16 / J1). A wall removed with Remove wall
 * records the distance each room grew (2 in each for a 4 in wall), so it never reads as
 * a split line. The record is on both rooms' copies; either is enough.
 */
import { POLICY } from './policy';
import { EPS, axisOf, edgeOf, findRoom, outN } from './geom';
import { buildUF, compOf, isMeasured, type UF } from './uf';
import { partnerOf, sharedMap } from './shared';
import { findOpposite } from './opposite';
import { lineGroup, moveLine } from './lineShift';
import type { Axis, Fail, Plan, Room, SharedMap } from './types';

export interface WallLine { r: Room; ids: Set<number>; at: number }

/** Every room's line that is the same wall: rooms across walls on the line, and theirs, transitively (J17). */
export function wallLines(plan: Plan, r: Room, vid: number, ax: Axis, M?: SharedMap): WallLine[] {
  const SM = M ?? sharedMap(plan);
  const lines: WallLine[] = [];
  const todo: { r: Room; vid: number }[] = [{ r, vid }];
  while (todo.length) {
    const { r: room, vid: v } = todo.pop()!;
    if (lines.some((l) => l.r === room && l.ids.has(v))) continue;
    const ids = lineGroup(room, v, ax);
    lines.push({ r: room, ids, at: room.corners.find((c) => c.id === v)![ax] });
    SM.get(room.id)?.forEach((ivs, w) => {
      const E = edgeOf(room, w);
      if (!E || !ids.has(w) || !ids.has(E.b.id)) return;
      ivs.forEach((iv) => {
        const q = findRoom(plan, iv.orid);
        if (q) todo.push({ r: q, vid: iv.ovid });
      });
    });
  }
  return lines;
}

/** Wall `vid` of `r` is the split line with room `orid` (see the header). */
export function isSplitLine(plan: Plan, r: Room, vid: number, orid: string): boolean {
  return r.openings.some((o) => {
    if (o.edgeStart !== vid || o.kind !== 'missing') return false;
    const ab = o.missing?.absorbed?.length ? o.missing.absorbed : partnerOf(plan, r, o)?.o.missing?.absorbed;
    return !!ab?.length && ab.every((a) => a.distanceIn === 0)
      && ab.some((a) => a.roomId === orid) && ab.some((a) => a.roomId === r.id);
  });
}

/** One side of a two-wall measurement (corner p's group or q's) and the rooms holding it. */
export interface HeldSide {
  /** True for q's side (the far wall / the blue end). */
  moveQ: boolean;
  /** The wall on this side the screen names (start corner id). */
  vid: number;
  /** Rooms across this side's shared walls, by name. */
  names: string[];
  /** One of them is the room this room was split from (or split off), across the split line. */
  split: boolean;
}

/** A choice the screen offers when neither wall is a split line (K15 case 2). */
export interface HeldChoice { moveQ: boolean; vid: number; label: string }

/** Shared walls of `r` on line axis `ax` whose corners are in coordinate group `root`. */
function heldWalls(r: Room, U: UF, ax: Axis, root: number, M: SharedMap): number[] {
  const out: number[] = [];
  M.get(r.id)?.forEach((_ivs, vid) => {
    const E = edgeOf(r, vid);
    if (!E) return;
    const t = axisOf(E.a, E.b);
    if ((t === 'h' ? 'y' : t === 'v' ? 'x' : null) === ax && U.find(ax, vid) === root) out.push(vid);
  });
  return out;
}

/** Both sides of the measurement between corners p and q on axis ax. */
export function heldSides(plan: Plan, r: Room, U: UF, ax: Axis, p: number, q: number, M: SharedMap): HeldSide[] {
  return [p, q].map((c, i) => {
    const walls = heldWalls(r, U, ax, U.find(ax, c), M);
    const own = walls.find((w) => w === c || edgeOf(r, w)?.b.id === c) ?? walls[0] ?? c;
    const names: string[] = [];
    let split = false;
    walls.forEach((w) => M.get(r.id)?.get(w)?.forEach((iv) => {
      const name = findRoom(plan, iv.orid)?.name ?? iv.orid;
      if (!names.includes(name)) names.push(name);
      if (isSplitLine(plan, r, w, iv.orid)) split = true;
    }));
    return { moveQ: i === 1, vid: own, names, split };
  });
}

/** K15 case 1: the one side across a split line, or null when the user must choose. */
export function splitSide(sides: HeldSide[]): HeldSide | null {
  const s = sides.filter((x) => x.split);
  return s.length === 1 ? s[0] : null;
}

export function heldChoices(sides: HeldSide[]): HeldChoice[] {
  return sides.map((s) => ({ moveQ: s.moveQ, vid: s.vid, label: `The wall to ${s.names.join(' and ')}` }));
}

export type HeldMoveResult = { ok: true; rooms: string[] } | (Fail<'measured' | 'too-small'> & { rid: string });

/**
 * Move this room's coordinate group of corner `vid` on axis `ax` by `delta` (feet, on
 * the axis), and every attached room's line on the same wall by the same amount, so
 * each of them changes by `delta` the other way (J17). Refused, with nothing changed,
 * when a room across has that size measured or would drop below `room_min_ft`.
 */
export function moveHeldSide(
  plan: Plan, r: Room, U: UF, ax: Axis, vid: number, delta: number, M: SharedMap,
): HeldMoveResult {
  const grp = compOf(r, U, ax, vid);
  const lines: WallLine[] = [];
  for (const w of heldWalls(r, U, ax, U.find(ax, vid), M)) {
    for (const l of wallLines(plan, r, w, ax, M)) {
      if (l.r === r) continue;
      if (!lines.some((x) => x.r === l.r && [...l.ids].every((id) => x.ids.has(id)))) lines.push(l);
    }
  }
  for (const l of lines) {
    if (isMeasured(l.r, buildUF(l.r), ax, [...l.ids][0])) {
      return {
        ok: false, err: 'measured', rid: l.r.id,
        message: `${l.r.name}'s size this way is measured, so that wall can't move. Remeasure ${l.r.name}, or move the other wall.`,
      };
    }
    for (const id of l.ids) {
      const W = edgeOf(l.r, id);
      if (!W || !l.ids.has(W.b.id)) continue;
      const opp = findOpposite(l.r, id);
      if (opp && opp.d + delta * outN(l.r, W.a, W.b)[ax] < POLICY.room_min_ft - EPS) {
        return { ok: false, err: 'too-small', rid: l.r.id, message: `${l.r.name} would be under ${POLICY.room_min_ft} ft. Move the other wall.` };
      }
    }
  }
  grp.forEach((c) => { c[ax] += delta; });
  for (const l of lines) moveLine(l.r, l.ids, ax, l.at + delta);
  return { ok: true, rooms: [r.id, ...new Set(lines.map((l) => l.r.id))] };
}
